import React from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../components/Header";
import MarkdownEditor from "../../components/MarkdownEditor";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { CREATE_INTERACTION } from "../../graph/interactions/mutations";
import useGraphQLClient from "../../hooks/useGraphQLClient";
import {
  interactionTypeOptions,
  interactionDirectionOptions,
  valueActionOptions,
  interactionTypeValues,
  interactionDirectionValues,
  valueActionValues,
} from "../../config/exportFormOptions.js";

const CreateInteractionForm = () => {
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const navigate = useNavigate();
  const location = useLocation();
  const client = useGraphQLClient();
  const { setSnackbar } = useSnackbar();

  const contactID = location.state?.contactID || "";
  const redirectPath = location.state?.redirectPath || "/manageInteractions";

  const handleFormSubmit = async (values, { resetForm }) => {
    try {
      const input = {
        contactID: values.contactID,
        type: values.type,
        direction: values.direction,
        summary: values.summary || null,
        notes: values.notes || null,
        meaningful: values.meaningful,
        valueAction: values.valueAction || null,
      };

      await client.request(CREATE_INTERACTION, { input });

      setSnackbar({
        open: true,
        message: "Interaction created successfully!",
        severity: "success",
      });

      resetForm();
      navigate(redirectPath);
    } catch (err) {
      const message = err?.response?.errors?.[0]?.message || "Unexpected error";

      console.error("GraphQL Error:", err);
      console.error("GraphQL response errors:", err?.response?.errors);

      setSnackbar({
        open: true,
        message: `Failed to create interaction: ${message}`,
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Header title="CREATE INTERACTION" subtitle="Create a New Interaction" />

      <Formik
        onSubmit={handleFormSubmit}
        initialValues={{
          contactID,
          type: "",
          direction: "",
          summary: "",
          notes: "",
          meaningful: true,
          valueAction: "",
        }}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              <TextField
                fullWidth
                variant="filled"
                label="Contact ID"
                name="contactID"
                value={values.contactID}
                disabled
                error={!!touched.contactID && !!errors.contactID}
                helperText={touched.contactID && errors.contactID}
                sx={{ gridColumn: "span 4" }}
              />

              <FormControl
                fullWidth
                variant="filled"
                sx={{ gridColumn: "span 2" }}
                error={!!touched.type && !!errors.type}
              >
                <InputLabel>Interaction Type</InputLabel>
                <Select
                  name="type"
                  value={values.type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {interactionTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                variant="filled"
                sx={{ gridColumn: "span 2" }}
                error={!!touched.direction && !!errors.direction}
              >
                <InputLabel>Direction</InputLabel>
                <Select
                  name="direction"
                  value={values.direction}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {interactionDirectionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                variant="filled"
                sx={{ gridColumn: "span 4" }}
                error={!!touched.valueAction && !!errors.valueAction}
              >
                <InputLabel>Value Action</InputLabel>
                <Select
                  name="valueAction"
                  value={values.valueAction}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <MenuItem value="">None</MenuItem>
                  {valueActionOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                variant="filled"
                label="Summary"
                name="summary"
                value={values.summary}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!touched.summary && !!errors.summary}
                helperText={touched.summary && errors.summary}
                sx={{ gridColumn: "span 4" }}
              />

              <MarkdownEditor
                value={values.notes}
                onChange={(nextValue) => setFieldValue("notes", nextValue)}
                label="Notes"
                minRows={14}
                error={!!touched.notes && !!errors.notes}
                helperText={touched.notes && errors.notes}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box sx={{ gridColumn: "span 4" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.meaningful}
                    onChange={(e) => setFieldValue("meaningful", e.target.checked)}
                  />
                }
                label="Meaningful interaction"
              />
            </Box>

            <Box display="flex" justifyContent="flex-end" mt="20px">
              <Button type="submit" color="secondary" variant="contained">
                Create Interaction
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const validationSchema = yup.object().shape({
  contactID: yup.string().required("Required"),
  type: yup.string().oneOf(interactionTypeValues).required("Required"),
  direction: yup.string().oneOf(interactionDirectionValues).required("Required"),
  valueAction: yup.string().oneOf(["", ...valueActionValues]).nullable().notRequired(),
  summary: yup.string().required("Required"),
  notes: yup.string().nullable().notRequired(),
  meaningful: yup.boolean().required(),
});

export default CreateInteractionForm;